import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { RelatedProductsArea } from "web/react/components/__LEGACY__/LEGACY_related-products-area/related-products-area";
import CuratedAds from "web/react/components/__LEGACY__/curated-ads/curated-ads";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import { AppFooter } from "web/react/components/app-footer/app-footer";
import { BrandAdContainer } from "web/react/components/brand-ad-container";
import { Breadcrumbs } from "web/react/components/breadcrumbs/breadcrumbs";
import BuyArea from "web/react/components/buybuybuy-area/buy-area/buy-area";
import Conditional from "web/react/components/conditional/conditional";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { FooterProps } from "web/react/components/footer/footer";
import PDPGallery from "web/react/components/pdp-gallery/pdp-gallery";
import { ProductOverlay } from "web/react/components/product-overlay";
import { RecentlyViewed } from "web/react/components/recently-viewed/recently-viewed";
import { RelatedDesignersRail } from "web/react/components/related-designers/related-designers-rail";
import { RelatedProductsSidebar } from "web/react/components/related-products-sidebar/related-products-sidebar";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { VStack } from "web/react/emo/v-stack";
import layout from "web/react/pages/product/in-stock/LEGACY/in-stock-layout.module.css";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import { canUseMembership } from "web/script/modules/utils";
import { InStockIconProductLayoutSerializer } from "web/types/serializers";
import styles from "./in-stock-checkout-product-page.module.css";

interface InStockCheckoutProductPageProps extends FooterProps {
    productLayout: InStockIconProductLayoutSerializer;
    csfr: string;
}

function InStockCheckoutProductPage({
    productLayout,
    csfr,
    helpCentreURLs,
    country,
}: InStockCheckoutProductPageProps): React.ReactElement {
    const overlayRef = useRef(null);

    const {
        product_analytics_data: productAnalyticsData,
        product_overview: productOverview,
        related_products_sidebar: relatedProductsSidebar,
        product_edit_button: productEditButton,
        adsense_for_shopping: adsenseForShopping,
        brand_ad: brandAd,
        related_products: relatedProducts,
        related_designers_category_rail: relatedDesignersCategoryRail,
        related_search_rail: relatedSearchRail,
        product_breadcrumbs: productBreadcrumbs,
        recently_viewed: recentlyViewedProducts,
        curated_ads: curatedAds,
        retailer_slug: retailerSlug,
        footer,
    } = productLayout;

    useEffect(() => {
        userProfiler.saveSeenProduct(productAnalyticsData);
    }, [productAnalyticsData]);

    const isLoggedIn = userProfiler.isLoggedIn();

    const isAdsenseAtTop = !(curatedAds && brandAd);

    return (
        <div className={layout.container}>
            <CustomerCaptureOverlay />

            <div ref={overlayRef}>
                <ProductOverlay parentRef={overlayRef} />
            </div>

            <div className={layout.primary}>
                <article
                    className={clsx(layout.info, {
                        [layout.infoWithSidebar]: !!relatedProductsSidebar,
                    })}
                >
                    <div className={layout.gallery}>
                        <Conditional
                            check={productEditButton !== null && productEditButton.is_user_staff}
                            key="edit-button"
                        >
                            <a
                                href={`https://os.lystit.com/product-data/product/${productEditButton.product_slug}/${productEditButton.product_id}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={layout.edit}
                            >
                                <button className="button black">
                                    {gettext("product_edit_button.label")}
                                </button>
                            </a>
                        </Conditional>

                        <PDPGallery
                            /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                            product={productOverview!.product_image_gallery}
                            hasRelatedProductsSidebar={!!relatedProductsSidebar}
                        />
                    </div>

                    <div className={layout.details}>
                        {productOverview?.save_for_later &&
                            productOverview.buybuybuy_area?.product_id &&
                            canUseMembership() && (
                                <SaveForLaterButton
                                    cta={
                                        productOverview.save_for_later.save_for_later_signup_prompt
                                            ?.cta_text
                                    }
                                    pageSource="pdp"
                                    productId={productOverview.buybuybuy_area?.product_id.toString()}
                                    isLoggedIn={isLoggedIn}
                                    type="LEGACY_small"
                                    className={styles.saveForLater}
                                    isCheckout={true}
                                />
                            )}
                        {productOverview?.buybuybuy_area && (
                            <BuyArea
                                header={productOverview.product_header}
                                buyAreaOptions={productOverview.buybuybuy_area}
                                productDescription={productOverview.product_description}
                                csrfToken={csfr}
                                productImages={productOverview.product_image_gallery.images}
                            />
                        )}
                        {productOverview?.related_products && (
                            <div>
                                <div>
                                    <RelatedProductsSidebar
                                        relatedProductsSidebar={productOverview.related_products}
                                        variant="horizontal"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </article>

                {relatedProductsSidebar && (
                    <RelatedProductsSidebar
                        relatedProductsSidebar={relatedProductsSidebar}
                        variant="vertical"
                        className={clsx(layout.sidebar, "affiliateSidebar")}
                    />
                )}
            </div>
            <div className={layout.secondary}>
                <VStack spacing="xl">
                    {brandAd && <BrandAdContainer {...brandAd} />}
                    {curatedAds && <CuratedAds {...curatedAds} usesFeedLayout={false} />}
                    {adsenseForShopping && isAdsenseAtTop && (
                        <AdsenseShopping {...adsenseForShopping} />
                    )}
                </VStack>
                {relatedProducts && (
                    <RelatedProductsArea
                        onOOS={false}
                        sections={relatedProducts.sections}
                        isAffiliate={false}
                        retailerSlug={retailerSlug}
                    />
                )}
                {adsenseForShopping && !isAdsenseAtTop && (
                    <AdsenseShopping {...adsenseForShopping} />
                )}
            </div>

            {relatedDesignersCategoryRail && !!relatedDesignersCategoryRail.designers.length && (
                <RelatedDesignersRail
                    title={relatedDesignersCategoryRail.title}
                    designers={relatedDesignersCategoryRail.designers}
                    showImages={false}
                />
            )}

            {relatedSearchRail && !!relatedSearchRail.related_searches.length && (
                <div className={clsx(layout.secondary, layout.marginLeft)}>
                    <RelatedSearchesRail
                        title={gettext("product.related_products.related_searches")}
                        relatedSearches={relatedSearchRail?.related_searches}
                    />
                </div>
            )}

            {productBreadcrumbs && (
                <div className={clsx(layout.secondary, layout.marginLeft)}>
                    <Breadcrumbs breadcrumbs={productBreadcrumbs.breadcrumbs} type="pdp" />
                </div>
            )}
            {recentlyViewedProducts && (
                <div className={layout.secondary}>
                    <RecentlyViewed
                        title={recentlyViewedProducts.title}
                        products={recentlyViewedProducts.products}
                        is_loading={recentlyViewedProducts.is_loading}
                        current_product_id={recentlyViewedProducts.current_product_id}
                        in_overlay={recentlyViewedProducts.in_overlay}
                        isRedesign={false}
                    />
                </div>
            )}

            <AppFooter data={footer} helpCentreURLs={helpCentreURLs} country={country} />
        </div>
    );
}

export default InStockCheckoutProductPage;
